// @ts-strict

import { formatDate } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StatisticsGraphsService } from '@services/statistics/statistics-graphs.service';
import { NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { GraphFilterOption, StatisticsItem, StatisticsMapItem } from '@pages/statistics/shared/interfaces/statistics';
import { StatisticsSummaryService } from '@services/statistics/statistics-summary.service';
import { ChartDataset } from 'chart.js';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { SelectDataMenuComponent } from './select-data-menu/select-data-menu.component';
import { StatisticsMapsGraphService } from './statistics-maps-graph/statistics-maps-graph.service';

@Component({
  selector: 'app-statistics-graphs',
  templateUrl: './statistics-graphs.component.html',
  styleUrls: ['./statistics-graphs.component.scss']
})
export class StatisticsGraphsComponent implements OnInit, OnDestroy {
  @ViewChild(SelectDataMenuComponent) child!: SelectDataMenuComponent;

  @Input() graphFilterOptions: GraphFilterOption[] = [];
  @Input() section = '';
  @Input() title = '';
  @Input() graphAndMapContainerTitle = '';
  @Input() graphAndMapContainerTotalElements = '0';
  @Input() graphAndMapContainerHeaderIcon = '';

  @Input() mapData: StatisticsMapItem[] = [];
  @Input() mapCompareData: StatisticsMapItem[] = [];

  public compareMode = false;
  public isSelectMenuOpen = false;
  public resetFilters = false;
  public isContainerExpanded = false;
  public isGraphDataLoading = false;
  public isMapDataLoading = false;
  public currentActiveMode = '';
  public userCurrency = '';

  public currentSelectedData: GraphFilterOption[] = [];
  public columnsData = [];

  public dataTable: StatisticsItem[] = [];

  public graphData: ChartDataset[] = [];
  public graphLabels: string[] = [];

  public switchButtons = [
    { id: 'graph', name: 'graph' },
    { id: 'map', name: 'map' }
  ];

  public dates?: { dateFrom: string; dateTo: string };
  public compareDates?: { dateFrom: string; dateTo: string };

  private compareModeOn = [
    'stats_daily',
    'campaigns_details_stats',
    'configuration_stats',
    'leads',
    'visits',
    'countries',
    'smartlinks_stats',
    'incent_campaigns_stats'
  ];

  private destroy$ = new Subject<void>();

  private initialDate = {
    dateFrom: this.calendar.getPrev(this.calendar.getToday(), 'd', 30),
    dateTo: this.calendar.getToday(),
    picker: 'general',
    selectedRange: 'last_30_days'
  };

  constructor(
    private graphsDataService: StatisticsGraphsService,
    private readonly statistics: StatisticsSummaryService,
    private readonly calendar: NgbCalendar,
    private readonly statisticsMapsGraphService: StatisticsMapsGraphService
  ) {}

  ngOnInit(): void {
    this.setSelectedData([...this.graphFilterOptions.filter(filter => filter.status).map(item => ({ ...item, compare: false }))]);

    this.graphsDataService.isLoading$
      .pipe(
        tap(isLoading => {
          this.isGraphDataLoading = isLoading;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statisticsMapsGraphService
      .getIsLoading()
      .pipe(
        tap(isLoading => (this.isMapDataLoading = isLoading)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    if (this.compareModeOn.includes(this.title)) {
      combineLatest([this.statistics.compareMode, this.graphsDataService.graphCompareData$, this.statistics.dateRange, this.graphsDataService.graphData$])
        .pipe(
          tap(([compareMode, graphCompareData, dateRange, graphData]) => {
            this.compareMode = compareMode.compareMode;

            const from = formatDate(new Date(dateRange.dateFrom.year, dateRange.dateFrom.month - 1, dateRange.dateFrom.day), 'yyyy-MM-dd', 'en');

            const to = formatDate(new Date(dateRange.dateTo.year, dateRange.dateTo.month - 1, dateRange.dateTo.day), 'yyyy-MM-dd', 'en');

            this.dates = { dateFrom: from, dateTo: to };

            if (!this.compareMode) {
              this.currentSelectedData = this.currentSelectedData.filter(item => !item.compare);

              const dataTable = this.graphsDataService.createDataTable(from, to, graphData);
              const { graphLabels, preparedGraphData } = this.graphsDataService.prepareGraphData(this.currentSelectedData, dataTable);

              this.dataTable = dataTable;
              this.graphData = preparedGraphData;
              this.graphLabels = graphLabels;
            }

            if (this.compareMode && compareMode.dateRange && compareMode.dateRange.dateFrom && compareMode.dateRange.dateTo) {
              this.setSelectedData(this.currentSelectedData.filter(item => !item.compare));

              const compareFrom = formatDate(
                new Date(compareMode.dateRange.dateFrom.year, compareMode.dateRange.dateFrom.month - 1, compareMode.dateRange.dateFrom.day),
                'yyyy-MM-dd',
                'en'
              );
              const compareTo = formatDate(
                new Date(compareMode.dateRange.dateTo.year, compareMode.dateRange.dateTo.month - 1, compareMode.dateRange.dateTo.day),
                'yyyy-MM-dd',
                'en'
              );

              this.compareDates = { dateFrom: compareFrom, dateTo: compareTo };

              const dataTable = this.graphsDataService.createDataTable(from, to, graphData);
              const compareDataTable = this.graphsDataService.createDataTable(compareFrom, compareTo, graphCompareData);

              const { graphLabels } = this.graphsDataService.prepareGraphData(this.currentSelectedData, dataTable);
              const { graphLabels: graphCompareLabels } = this.graphsDataService.prepareGraphData(this.currentSelectedData, compareDataTable);

              compareDataTable.map(item => (item.compare = true));

              const { preparedGraphData } = this.graphsDataService.prepareGraphData(this.currentSelectedData, [...dataTable, ...compareDataTable]);

              this.dataTable = [...dataTable, ...compareDataTable];
              this.graphData = preparedGraphData;

              this.graphLabels = graphLabels.map((label, index) => {
                if (graphCompareLabels[index]) {
                  return `${label}#${graphCompareLabels[index]}`;
                }

                return `${label}`;
              });
            }
          }),
          takeUntil(this.destroy$)
        )
        .subscribe();
    }

    this.isContainerExpanded = true;

    combineLatest([this.statistics.dateRange, this.graphsDataService.graphData$])
      .pipe(
        tap(([dateRange, graphData]) => {
          if (!this.compareMode) {
            const from = formatDate(new Date(dateRange.dateFrom.year, dateRange.dateFrom.month - 1, dateRange.dateFrom.day), 'yyyy-MM-dd', 'en');
            const to = formatDate(new Date(dateRange.dateTo.year, dateRange.dateTo.month - 1, dateRange.dateTo.day), 'yyyy-MM-dd', 'en');

            this.dates = { dateFrom: from, dateTo: to };

            this.userCurrency = JSON.parse(localStorage.getItem('basic_user_data') ?? '')?.user.currency;
            this.dataTable = this.graphsDataService.createDataTable(from, to, graphData);

            const { graphLabels, preparedGraphData } = this.graphsDataService.prepareGraphData(this.currentSelectedData, this.dataTable);

            this.graphData = preparedGraphData;
            this.graphLabels = graphLabels;
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statistics.dateRange.next(this.initialDate);
  }

  toggleContainer(): void {
    this.isContainerExpanded = !this.isContainerExpanded;
    const switchBtns = document.querySelector('.switch-container');
    if (this.isContainerExpanded) {
      switchBtns!.classList.remove('displayNone');
    } else {
      switchBtns!.classList.add('displayNone');
    }
  }

  toggleSelectDataMenu(): void {
    this.isSelectMenuOpen = !this.isSelectMenuOpen;
  }

  closeMenu(status: boolean): void {
    this.isSelectMenuOpen = !status;
  }

  setSelectedData(selectedData: GraphFilterOption[]): void {
    this.resetFilters = true;

    if (this.compareMode) {
      this.currentSelectedData = [...selectedData.map(item => ({ ...item, compare: false })), ...selectedData.map(item => ({ ...item, compare: true }))];
    } else {
      this.currentSelectedData = selectedData;
    }

    const { preparedGraphData } = this.graphsDataService.prepareGraphData(this.currentSelectedData, this.dataTable);

    this.graphData = preparedGraphData;
  }

  ngOnDestroy(): void {
    this.resetFilters = false;
    this.destroy$.next();
    this.destroy$.complete();
  }
}
